// Enhanced authentication helper for remember me functionality

interface RememberMeData {
  email: string;
  rememberMe: boolean;
  lastLogin?: string;
}

// Constants for localStorage keys
const AUTH_TOKEN_KEY = 'authToken';
const REMEMBER_ME_EMAIL_KEY = 'adminEmail';
const REMEMBER_ME_FLAG_KEY = 'adminRememberMe';
const REMEMBER_ME_DATA_KEY = 'adminRememberMeData';
const LAST_LOGIN_KEY = 'adminLastLogin';

export class AuthHelper {
  
  // Save remember me data
  static saveRememberMeData(email: string, rememberMe: boolean) {
    if (rememberMe) {
      const data: RememberMeData = {
        email: email.trim(),
        rememberMe: true,
        lastLogin: new Date().toISOString()
      };
      
      localStorage.setItem(REMEMBER_ME_EMAIL_KEY, email.trim());
      localStorage.setItem(REMEMBER_ME_FLAG_KEY, 'true');
      localStorage.setItem(REMEMBER_ME_DATA_KEY, JSON.stringify(data));
      localStorage.setItem(LAST_LOGIN_KEY, new Date().toISOString());
    } else {
      // Clear remember me data
      localStorage.removeItem(REMEMBER_ME_EMAIL_KEY);
      localStorage.removeItem(REMEMBER_ME_FLAG_KEY);
      localStorage.removeItem(REMEMBER_ME_DATA_KEY);
    }
  }

  // Get remember me data
  static getRememberMeData(): RememberMeData | null {
    try {
      const flag = localStorage.getItem(REMEMBER_ME_FLAG_KEY);
      const email = localStorage.getItem(REMEMBER_ME_EMAIL_KEY);
      const dataStr = localStorage.getItem(REMEMBER_ME_DATA_KEY);
      
      if (flag === 'true' && email) {
        if (dataStr) {
          return JSON.parse(dataStr);
        }
        
        // Fallback to old format
        return {
          email,
          rememberMe: true
        };
      }
      
      return null;
    } catch (error) {
      console.warn('Error parsing remember me data:', error);
      return null;
    }
  }

  // Save auth token
  static saveAuthToken(token: string) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }

  // Get auth token
  static getAuthToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  // Check if user has valid token
  static hasValidToken(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;

    try {
      // Basic JWT validation (check if it's not expired)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp ? payload.exp > currentTime : true;
    } catch (error) {
      // If token parsing fails, consider it invalid
      return false;
    }
  }

  // Clear all auth data
  static clearAuthData() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.clear();
  }

  // Clear remember me data only
  static clearRememberMeData() {
    localStorage.removeItem(REMEMBER_ME_EMAIL_KEY);
    localStorage.removeItem(REMEMBER_ME_FLAG_KEY);
    localStorage.removeItem(REMEMBER_ME_DATA_KEY);
  }

  // Get last login time
  static getLastLoginTime(): Date | null {
    const lastLogin = localStorage.getItem(LAST_LOGIN_KEY);
    return lastLogin ? new Date(lastLogin) : null;
  }

  // Check if remember me session is too old (optional feature)
  static isRememberMeExpired(maxDays: number = 30): boolean {
    const lastLogin = this.getLastLoginTime();
    if (!lastLogin) return true;

    const daysSinceLogin = (Date.now() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceLogin > maxDays;
  }
}